// Google Drive Service
// Integração com Google Drive API e Google Picker
// Com persistência de tokens no Supabase

import { supabase } from '../lib/supabase';
import { systemConfigService } from './system-config-service';

const SCOPES = 'https://www.googleapis.com/auth/drive.readonly';
const PROVIDER = 'google_drive';

// Função para obter Client ID (Vault > ENV)
const getGoogleClientId = (): string => {
  // Primeiro tenta do cache do systemConfigService
  const fromVault = systemConfigService.getSync('GOOGLE_CLIENT_ID');
  if (fromVault) return fromVault;
  
  // Fallback para variável de ambiente
  return import.meta.env.VITE_GOOGLE_CLIENT_ID || '';
};

// Função para obter API Key (Vault > ENV)
const getGoogleApiKey = (): string => {
  const fromVault = systemConfigService.getSync('GOOGLE_API_KEY');
  if (fromVault) return fromVault;
  
  return import.meta.env.VITE_GOOGLE_API_KEY || '';
};

interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: number;
  iconUrl?: string;
  thumbnailUrl?: string;
}

interface PickerResult {
  action: string;
  docs?: GoogleDriveFile[];
}

interface UserIntegration {
  id: string;
  user_id: string;
  provider: string;
  access_token: string;
  refresh_token?: string;
  token_expiry?: string;
  provider_email?: string;
  is_active: boolean;
}

class GoogleDriveService {
  private accessToken: string | null = null;
  private currentUserId: string | null = null;
  private pickerInited = false;
  private gapiInited = false;

  // Carregar scripts do Google
  async loadGoogleScripts(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!document.getElementById('google-api-script')) {
        const apiScript = document.createElement('script');
        apiScript.id = 'google-api-script';
        apiScript.src = 'https://apis.google.com/js/api.js';
        apiScript.async = true;
        apiScript.defer = true;
        apiScript.onload = () => {
          window.gapi.load('picker', () => {
            this.gapiInited = true;
            this.pickerInited = true;
            resolve();
          });
        };
        apiScript.onerror = reject;
        document.head.appendChild(apiScript);
      } else if (window.gapi) {
        window.gapi.load('picker', () => {
          this.gapiInited = true;
          this.pickerInited = true;
          resolve();
        });
      } else {
        resolve();
      }
    });
  }

  // Carregar token salvo do Supabase
  async loadSavedToken(userId: string): Promise<string | null> {
    try {
      const { data, error } = await supabase
        .from('user_integrations')
        .select('*')
        .eq('user_id', userId)
        .eq('provider', PROVIDER)
        .eq('is_active', true)
        .single();

      if (error || !data) {
        console.log('Nenhum token salvo encontrado para Google Drive');
        return null;
      }

      // Verificar se o token expirou
      if (data.token_expiry) {
        const expiry = new Date(data.token_expiry);
        if (expiry < new Date()) {
          console.log('Token expirado, necessário novo login');
          return null;
        }
      }

      this.accessToken = data.access_token;
      this.currentUserId = userId;
      console.log('✅ Token do Google Drive carregado do banco');
      return data.access_token;
    } catch (error) {
      console.error('Erro ao carregar token:', error);
      return null;
    }
  }

  // Salvar token no Supabase
  async saveToken(userId: string, accessToken: string, expiresIn?: number): Promise<void> {
    try {
      const tokenExpiry = expiresIn 
        ? new Date(Date.now() + expiresIn * 1000).toISOString()
        : new Date(Date.now() + 3600 * 1000).toISOString(); // Default 1 hora

      const { error } = await supabase
        .from('user_integrations')
        .upsert({
          user_id: userId,
          provider: PROVIDER,
          access_token: accessToken,
          token_expiry: tokenExpiry,
          is_active: true,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,provider'
        });

      if (error) {
        console.error('Erro ao salvar token:', error);
        throw error;
      }

      console.log('✅ Token do Google Drive salvo no banco');
    } catch (error) {
      console.error('Erro ao salvar token:', error);
    }
  }

  // Remover integração (desconectar)
  async disconnect(userId: string): Promise<void> {
    try {
      await supabase
        .from('user_integrations')
        .delete()
        .eq('user_id', userId)
        .eq('provider', PROVIDER);

      this.accessToken = null;
      this.currentUserId = null;
      console.log('✅ Google Drive desconectado');
    } catch (error) {
      console.error('Erro ao desconectar:', error);
    }
  }

  // Verificar se está conectado
  async isConnected(userId: string): Promise<boolean> {
    if (this.accessToken && this.currentUserId === userId) {
      return true;
    }
    const token = await this.loadSavedToken(userId);
    return !!token;
  }

  // Obter token de acesso via popup OAuth
  async getAccessToken(userId: string): Promise<string> {
    // Primeiro, tentar carregar token salvo
    const savedToken = await this.loadSavedToken(userId);
    if (savedToken) {
      return savedToken;
    }

    // Se não tem token salvo, fazer login
    return new Promise((resolve, reject) => {
      const clientId = getGoogleClientId();
      const authUrl = `https://accounts.google.com/o/oauth2/v2/auth?` +
        `client_id=${encodeURIComponent(clientId)}` +
        `&redirect_uri=${encodeURIComponent(window.location.origin)}` +
        `&response_type=token` +
        `&scope=${encodeURIComponent(SCOPES)}` +
        `&include_granted_scopes=true`;

      const width = 500;
      const height = 600;
      const left = window.screenX + (window.outerWidth - width) / 2;
      const top = window.screenY + (window.outerHeight - height) / 2;
      
      const popup = window.open(
        authUrl,
        'google-auth',
        `width=${width},height=${height},left=${left},top=${top}`
      );

      if (!popup) {
        reject(new Error('Popup bloqueado. Permita popups para este site.'));
        return;
      }

      const checkPopup = setInterval(() => {
        try {
          if (popup.closed) {
            clearInterval(checkPopup);
            reject(new Error('Autenticação cancelada'));
            return;
          }

          const popupUrl = popup.location.href;
          
          if (popupUrl.startsWith(window.location.origin)) {
            clearInterval(checkPopup);
            
            const hash = popup.location.hash;
            popup.close();
            
            if (hash) {
              const params = new URLSearchParams(hash.substring(1));
              const token = params.get('access_token');
              const expiresIn = params.get('expires_in');
              
              if (token) {
                this.accessToken = token;
                this.currentUserId = userId;
                
                // Salvar token no banco
                this.saveToken(userId, token, expiresIn ? parseInt(expiresIn) : undefined);
                
                resolve(token);
              } else {
                reject(new Error('Token não encontrado'));
              }
            } else {
              reject(new Error('Resposta inválida do Google'));
            }
          }
        } catch (e) {
          // Erro de cross-origin esperado enquanto estiver no Google
        }
      }, 500);

      setTimeout(() => {
        clearInterval(checkPopup);
        if (!popup.closed) {
          popup.close();
        }
        reject(new Error('Timeout na autenticação'));
      }, 120000);
    });
  }

  // Abrir Google Picker
  async openPicker(userId: string): Promise<GoogleDriveFile[]> {
    const clientId = getGoogleClientId();
    const apiKey = getGoogleApiKey();
    
    if (!clientId || !apiKey) {
      throw new Error('Credenciais do Google não configuradas. Configure VITE_GOOGLE_CLIENT_ID e VITE_GOOGLE_API_KEY no arquivo .env');
    }

    await this.loadGoogleScripts();
    const token = await this.getAccessToken(userId);

    return new Promise((resolve) => {
      const picker = new window.google.picker.PickerBuilder()
        .addView(window.google.picker.ViewId.DOCS)
        .addView(window.google.picker.ViewId.RECENTLY_PICKED)
        .setOAuthToken(token)
        .setDeveloperKey(apiKey)
        .setCallback((data: PickerResult) => {
          if (data.action === window.google.picker.Action.PICKED) {
            const files: GoogleDriveFile[] = data.docs?.map((doc: any) => ({
              id: doc.id,
              name: doc.name,
              mimeType: doc.mimeType,
              size: doc.sizeBytes,
              iconUrl: doc.iconUrl,
              thumbnailUrl: doc.thumbnails?.[0]?.url
            })) || [];
            resolve(files);
          } else if (data.action === window.google.picker.Action.CANCEL) {
            resolve([]);
          }
        })
        .setTitle('Selecionar arquivos do Google Drive')
        .setLocale('pt-BR')
        .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
        .build();

      picker.setVisible(true);
    });
  }

  // Baixar arquivo do Drive
  async downloadFile(fileId: string, fileName: string, mimeType: string): Promise<File> {
    if (!this.accessToken) {
      throw new Error('Não autenticado no Google Drive');
    }

    let downloadUrl: string;
    let exportMimeType = mimeType;

    if (mimeType === 'application/vnd.google-apps.document') {
      downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/pdf`;
      exportMimeType = 'application/pdf';
      fileName = fileName.replace(/\.[^/.]+$/, '') + '.pdf';
    } else if (mimeType === 'application/vnd.google-apps.spreadsheet') {
      downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/vnd.openxmlformats-officedocument.spreadsheetml.sheet`;
      exportMimeType = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
      fileName = fileName.replace(/\.[^/.]+$/, '') + '.xlsx';
    } else if (mimeType === 'application/vnd.google-apps.presentation') {
      downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}/export?mimeType=application/pdf`;
      exportMimeType = 'application/pdf';
      fileName = fileName.replace(/\.[^/.]+$/, '') + '.pdf';
    } else {
      downloadUrl = `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media`;
    }

    const response = await fetch(downloadUrl, {
      headers: {
        'Authorization': `Bearer ${this.accessToken}`
      }
    });

    if (!response.ok) {
      throw new Error(`Erro ao baixar arquivo: ${response.statusText}`);
    }

    const blob = await response.blob();
    return new File([blob], fileName, { type: exportMimeType });
  }

  // Selecionar e baixar arquivos
  async selectAndDownloadFiles(userId: string): Promise<File[]> {
    const selectedFiles = await this.openPicker(userId);
    
    if (selectedFiles.length === 0) {
      return [];
    }

    const downloadedFiles: File[] = [];
    
    for (const file of selectedFiles) {
      try {
        const downloadedFile = await this.downloadFile(file.id, file.name, file.mimeType);
        downloadedFiles.push(downloadedFile);
      } catch (error) {
        console.error(`Erro ao baixar ${file.name}:`, error);
      }
    }

    return downloadedFiles;
  }

  // Verificar se está configurado
  isConfigured(): boolean {
    const clientId = getGoogleClientId();
    const apiKey = getGoogleApiKey();
    return !!(clientId && apiKey);
  }
}

// Tipos para window
declare global {
  interface Window {
    google: any;
    gapi: any;
  }
}

export const googleDriveService = new GoogleDriveService();
export type { GoogleDriveFile };
